// NotificationPreferencesCard — per-type toggle and time override for every
// built-in notification (daily / weekly / context-driven trigger). Catalog
// comes from the backend so adding a new built-in type is one place.

import { useEffect, useMemo, useState } from 'react';
import { api, APIError } from '../lib/api';
import type { NotificationPreferenceRow } from '../lib/api';
import { Card } from './Card';
import { Chip } from './Chip';
import { CollapsibleSection } from './CollapsibleSection';
import { Modal } from './Modal';
import { TimePicker, formatHM } from './TimePicker';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function NotificationPreferencesCard() {
  const [rows, setRows] = useState<NotificationPreferenceRow[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<NotificationPreferenceRow | null>(null);

  const refresh = async () => {
    try {
      const r = await api.listNotificationPreferences();
      setRows(r.preferences);
    } catch (e) {
      setError(e instanceof APIError ? `Load: ${e.status}` : 'Load failed');
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const groups = useMemo(() => {
    const out: Record<'daily' | 'weekly' | 'trigger', NotificationPreferenceRow[]> = {
      daily: [],
      weekly: [],
      trigger: [],
    };
    for (const r of rows ?? []) out[r.group].push(r);
    return out;
  }, [rows]);

  const setEnabled = async (row: NotificationPreferenceRow, enabled: boolean) => {
    setBusy(row.type);
    setError(null);
    try {
      await api.upsertNotificationPreference(row.type, {
        enabled,
        custom_hour: row.override?.custom_hour,
        custom_minute: row.override?.custom_minute,
      });
      await refresh();
    } catch (e) {
      setError(e instanceof APIError ? `Save: ${e.status}` : 'Save failed');
    } finally {
      setBusy(null);
    }
  };

  const reset = async (row: NotificationPreferenceRow) => {
    setBusy(row.type);
    try {
      await api.resetNotificationPreference(row.type);
      await refresh();
    } catch (e) {
      setError(e instanceof APIError ? `Reset: ${e.status}` : 'Reset failed');
    } finally {
      setBusy(null);
    }
  };

  const renderRow = (row: NotificationPreferenceRow) => {
    const enabled = row.override?.enabled ?? true;
    const hour = row.override?.custom_hour ?? row.default_hour;
    const minute = row.override?.custom_minute ?? row.default_min;
    const isCustom = row.override?.custom_hour != null || row.override?.custom_minute != null;
    return (
      <div
        key={row.type}
        className="flex items-start gap-3 p-2.5 rounded-panel transition-colors hover:bg-fg/[0.04]"
      >
        <button
          type="button"
          onClick={() => setEnabled(row, !enabled)}
          disabled={busy === row.type}
          aria-label={enabled ? 'Disable' : 'Enable'}
          className={`mt-0.5 grid place-items-center size-5 rounded-[7px] border transition-all shrink-0 ${
            enabled
              ? 'bg-accent border-accent text-accent-fg'
              : 'bg-bg-sunken border-line-strong text-transparent'
          }`}
        >
          <svg viewBox="0 0 16 16" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 8.5l3.5 3.5L13 5" />
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <span className={`text-sm ${enabled ? 'text-fg' : 'text-fg-faint'}`}>{row.label}</span>
            {row.has_fixed_time && (
              <button
                type="button"
                onClick={() => setEditing(row)}
                disabled={busy === row.type}
                className={`font-mono text-[11px] tabular-nums shrink-0 underline-offset-2 hover:underline transition-colors ${
                  isCustom ? 'text-fg font-medium' : 'text-fg-faint'
                }`}
              >
                {formatHM(hour, minute)}
                {row.weekday >= 0 ? ` · ${WEEKDAY_LABELS[row.weekday]}` : ''}
              </button>
            )}
            {!row.has_fixed_time && (
              <Chip tone="muted">Triggered</Chip>
            )}
          </div>
          <p className="text-xs text-fg-faint mt-0.5 line-clamp-2">
            {row.description ?? row.default_body}
          </p>
        </div>
      </div>
    );
  };

  return (
    <>
      <Card
        title="Notifications"
        hint="Toggle each reminder; tap a time to customise. Reset reverts to defaults."
      >
        {error && <p className="text-xs text-danger">{error}</p>}
        {!rows && <p className="text-sm text-fg-muted">Loading…</p>}
        {rows && (
          <div className="space-y-2">
            <CollapsibleSection
              eyebrow="Daily"
              defaultOpen
              summary={`${groups.daily.filter((r) => r.override?.enabled !== false).length}/${groups.daily.length} on`}
            >
              <div className="space-y-0.5">{groups.daily.map(renderRow)}</div>
            </CollapsibleSection>
            <CollapsibleSection
              eyebrow="Weekly"
              summary={`${groups.weekly.filter((r) => r.override?.enabled !== false).length}/${groups.weekly.length} on`}
            >
              <div className="space-y-0.5">{groups.weekly.map(renderRow)}</div>
            </CollapsibleSection>
            <CollapsibleSection
              eyebrow="Smart triggers"
              summary={`${groups.trigger.filter((r) => r.override?.enabled !== false).length}/${groups.trigger.length} on`}
            >
              <div className="space-y-0.5">{groups.trigger.map(renderRow)}</div>
            </CollapsibleSection>
          </div>
        )}
      </Card>
      {editing && (
        <EditTimeModal
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
          onReset={async () => {
            await reset(editing);
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

function EditTimeModal({
  row,
  onClose,
  onSaved,
  onReset,
}: {
  row: NotificationPreferenceRow;
  onClose: () => void;
  onSaved: () => void;
  onReset: () => void;
}) {
  const [hour, setHour] = useState(row.override?.custom_hour ?? row.default_hour);
  const [minute, setMinute] = useState(row.override?.custom_minute ?? row.default_min);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.upsertNotificationPreference(row.type, {
        enabled: row.override?.enabled ?? true,
        custom_hour: hour,
        custom_minute: minute,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof APIError ? `Save: ${e.status}` : 'Save failed');
      setBusy(false);
    }
  };

  return (
    <Modal
      title={row.label}
      open
      onClose={onClose}
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            className="btn-secondary flex-1 py-2.5 text-xs font-mono uppercase tracking-[0.14em]"
            onClick={onReset}
            disabled={busy || row.override == null}
          >
            Reset to default
          </button>
          <button
            type="button"
            className="btn-primary flex-1 py-2.5 text-xs font-mono uppercase tracking-[0.14em]"
            onClick={save}
            disabled={busy}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-xs text-fg-faint">
          Default: {formatHM(row.default_hour, row.default_min)}
        </p>
        <div className="flex items-center justify-between">
          <span className="label-eyebrow">Time</span>
          <TimePicker hour={hour} minute={minute} onChange={(h, m) => { setHour(h); setMinute(m); }} />
        </div>
        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
